import React, { useState } from "react";
import { FaChevronDown, FaTrash } from "react-icons/fa";
import CompanySourceWindow from "./CompanySourceWindow";

const MANUAL_ENTRY_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

const formatManualEntryInsertedAt = (value) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "Added manually";
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "Added manually";
  }

  return `Added ${MANUAL_ENTRY_DATE_TIME_FORMATTER.format(date)}`;
};

const CompanySourcesPanel = ({
  companySourceGroups,
  onOpenSourceWindow,
  sourceInlineInputByDataset,
  sourceInlineErrorByDataset,
  onSourceInlineInputChange,
  onClearSourceInlineInput,
  onSubmitInlineSources,
  onRemoveSourceIds,
  isSaving,
  sourceWindowState,
  sourceDatasetOptions,
}) => {
  const [expandedManualGroups, setExpandedManualGroups] = useState({});

  const toggleManualGroup = (datasetKey) => {
    setExpandedManualGroups((current) => ({
      ...current,
      [datasetKey]: !current[datasetKey],
    }));
  };

  return (
    <div className="space-y-3 rounded-2xl border border-slate-800/90 bg-slate-950/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-100">Sources</p>
          <p className="mt-1 text-xs text-slate-400">
            Add source IDs by dataset. The editor validates each ID before saving it.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenSourceWindow}
          className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-colors hover:border-cyan-400 hover:text-white"
        >
          Add source
        </button>
      </div>

      {companySourceGroups.length ? (
        <div className="space-y-2">
          {companySourceGroups.map((group) => {
            const manualItems = group.items.filter((item) => item.removable);
            const isManualGroupOpen = Boolean(expandedManualGroups[group.datasetKey]);
            const manualEntriesId = `company-manual-entries-${group.datasetKey}`;

            return (
              <div
                key={`company-source-group-${group.datasetKey}`}
                className="rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{group.label}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {group.docs} source{group.docs === 1 ? "" : "s"}
                    </p>
                  </div>

                  {manualItems.length ? (
                    <button
                      type="button"
                      onClick={() => toggleManualGroup(group.datasetKey)}
                      aria-expanded={isManualGroupOpen}
                      aria-controls={manualEntriesId}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
                    >
                      <span>
                        {isManualGroupOpen
                          ? "Hide manual entries"
                          : `Manual entries (${manualItems.length})`}
                      </span>
                      <FaChevronDown
                        className={`h-3 w-3 transition-transform ${
                          isManualGroupOpen ? "rotate-180" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  ) : (
                    <span className="text-xs text-slate-500">No manual entries</span>
                  )}
                </div>

                {isManualGroupOpen && manualItems.length ? (
                  <div id={manualEntriesId} className="mt-3 space-y-2">
                    {manualItems.map((item) => (
                      <div
                        key={`${group.datasetKey}-${item.collectionName || "collection"}-${item.recordId}`}
                        className="flex items-start justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/80 px-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-100">
                            {item.recordId}
                          </p>
                          <p className="mt-1 text-xs text-slate-400">
                            {formatManualEntryInsertedAt(item.manualEntry?.insertedAt)}
                          </p>
                        </div>
                        {onRemoveSourceIds ? (
                          <button
                            type="button"
                            onClick={() =>
                              onRemoveSourceIds({
                                dataset: group.datasetKey,
                                sourceIds: [item.recordId],
                              })
                            }
                            aria-label={`Remove manual source ${item.recordId}`}
                            title="Remove manual link"
                            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/35 bg-rose-500/10 px-2.5 py-1 text-xs font-medium text-rose-100 transition-colors hover:border-rose-400 hover:bg-rose-500/15 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isSaving}
                          >
                            <span>Remove</span>
                            <FaTrash className="h-3 w-3" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="mt-3 space-y-2">
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                      Add IDs to this source bucket
                    </span>
                    <textarea
                      value={sourceInlineInputByDataset[group.datasetKey] || ""}
                      onChange={(event) =>
                        onSourceInlineInputChange(group.datasetKey, event.target.value)
                      }
                      aria-label={`Add IDs to ${group.label}`}
                      rows={2}
                      placeholder="One ID per line, or comma-separated"
                      className="min-h-[4.5rem] w-full rounded-lg border border-slate-700 bg-slate-950/80 px-3 py-2 text-sm text-slate-100 outline-none transition-all focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20"
                    />
                  </label>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {sourceInlineErrorByDataset[group.datasetKey] ? (
                      <p className="text-xs text-rose-200">
                        {sourceInlineErrorByDataset[group.datasetKey]}
                      </p>
                    ) : (
                      <span className="text-xs text-slate-500">
                        IDs are appended to the existing bucket for this dataset.
                      </span>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => onClearSourceInlineInput(group.datasetKey)}
                        className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-[11px] font-semibold text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => onSubmitInlineSources(group.datasetKey)}
                        disabled={isSaving}
                        className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-[11px] font-semibold text-cyan-100 transition-colors hover:border-cyan-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Add IDs
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-700 px-4 py-4 text-sm text-slate-400">
          No sources linked yet.
        </div>
      )}

      <CompanySourceWindow {...sourceWindowState} sourceDatasetOptions={sourceDatasetOptions} />
    </div>
  );
};

export default CompanySourcesPanel;
